import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { visuallyHidden } from "@mui/utils";
import {
  OfferTableActionsToolbarProps,
  OfferTableHeadProps,
  TableOrder,
} from "./types";
import useTranslation from "../../../../hooks/useTranslation";
import SearchBar from "../../../../components/SearchBar";
import {
  Alert,
  Button,
  ButtonGroup,
  CircularProgress,
  TextField,
} from "@mui/material";
import { parseDate } from "../../../../utils/helperFunctions";
import { cityMap } from "../../../../utils/cityCodes";
import { headCells } from "./helper";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";
import "./PendingOffers.scss";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { UserRoles } from "../../../../stores/AuthStore/types";
import Delegation from "../../../Delegation";
import { observer } from "mobx-react";
import {
  Offer,
  OfferOrderBy,
  OfferStatus,
} from "../../../../stores/OfferStore/types";
import offerStore from "../../../../stores/OfferStore";

const OfferTableHead = (props: OfferTableHeadProps) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Offer | null) => (event: React.MouseEvent<unknown>) => {
      property !== null && onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {props.headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const OfferTableActionsToolbar = (props: OfferTableActionsToolbarProps) => {
  const { t } = useTranslation();

  return (
    <Toolbar
      sx={{
        flexFlow: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 0 },
        pt: { xs: 1, sm: 0 },
        pb: { xs: 1, sm: 0 },
        pl: { sm: 2 },
        pr: { xs: 2, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        {t("offers")}
      </Typography>
      <Typography
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 50%" },
          width: { xs: "100%", sm: "auto" },
        }}
        color="inherit"
        variant="subtitle1"
        component="div"
        className="pt"
      >
        <SearchBar handleOnSearch={props.handleOnSearch} />
      </Typography>
    </Toolbar>
  );
};

const PendingOffers = observer(() => {
  const { t } = useTranslation();
  const [order, setOrder] = React.useState<TableOrder>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Offer>("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [keyword, setKeyword] = useState("");
  const [rows, setRows] = useState<Offer[]>([]);
  const [selected, setSelected] = useState<Offer | null>(null);
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [loading, setLoading] = useState(false);
  const [openOfferDetails, setOpenOfferDetails] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);

  const fetchRows = useCallback(() => {
    setLoading(true);
    offerStore
      .searchOffers(
        "",
        `${orderBy}${order.charAt(0).toUpperCase()}${order.slice(
          1
        )}` as OfferOrderBy,
        rowsPerPage,
        page,
        OfferStatus.NEW
      )
      .then((result) => {
        console.log(result);
        setRows(result.offers);
        setRowsCount(result.count);
      })
      .catch((error) => {
        console.error(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderBy, order, page, rowsPerPage, keyword]);

  useEffect(() => {
    fetchRows();
  }, [orderBy, order, page, rowsPerPage, keyword, fetchRows]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Offer
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeywordChange = (keyword: string) => {
    setKeyword(keyword);
  };

  const handleOnApprove = () => {
    setAction("approve");
  };

  const handleOnReject = () => {
    setAction("reject");
  };

  const handleReview = (e: React.MouseEvent, offerItem: Offer) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(offerItem);
    setOpenOfferDetails(true);
  };

  const handleConfirm = (offer: Offer, confirm: "approve" | "reject") => {
    setLoading(true);
    const status =
      confirm === "approve" ? OfferStatus.PENDING : OfferStatus.REJECTED;
    const reason =
      t("invalid-offer") === "invalid-offer"
        ? t("your-offer-rejected")
        : t("invalid-offer");
    offerStore
      .updateOfferStatus(
        offer,
        status,
        status === OfferStatus.REJECTED ? reason : ""
      )
      .then(() => {
        setPage(0);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.SUCCESS,
          message: "success",
        });
      })
      .catch((error: any) => {
        console.error(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: error.message || "An error occurred",
        });
      })
      .finally(() => {
        setLoading(false);
        fetchRows();
      });
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }} style={{ position: "relative" }}>
      {loading && (
        <div className="tableShadow">
          <CircularProgress />
        </div>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <OfferTableActionsToolbar
          handleOnSearch={handleSearchKeywordChange}
          handleOnApprove={handleOnApprove}
          handleOnReject={handleOnReject}
        />
        <TableContainer style={{ position: "relative" }}>
          {action !== null && <div className="tableShadow"></div>}
          <Table
            sx={{ minWidth: 600 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <OfferTableHead
              headCells={headCells(t)}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map((row, index) => {
                const labelId = `offers-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={(row as any)._id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell component="th" id={labelId} scope="row">
                      {row.caseId}
                    </TableCell>
                    <TableCell align="center">
                      <span className="pr mr hidden-sm">
                        {row.user?.email || (row as any).userId}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <span className="pr mr hidden-sm">
                        {row.lawyer?.email || (row as any).lawyerId}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      {parseDate(row.createDate || "")}
                    </TableCell>
                    <TableCell align={"right"}>
                      <Button
                        variant={"contained"}
                        onClick={(e) => handleReview(e, row)}
                      >
                        <i
                          className={"fal fa-file-alt"}
                          style={{ fontSize: "18px" }}
                        />
                        &nbsp;&nbsp;
                        <i
                          className={"fal fa-search"}
                          style={{ fontSize: "18px" }}
                        />
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        color={"error"}
                        onClick={() => handleConfirm(row, "reject")}
                        disabled={loading}
                      >
                        {t("reject")}
                      </Button>
                      <Button
                        onClick={() => handleConfirm(row, "approve")}
                        disabled={loading}
                      >
                        {t("approve")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {action === null && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={t("rows-per-page")}
            component="div"
            count={rowsCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
      <Dialog
        open={openOfferDetails}
        onClose={() => setOpenOfferDetails(false)}
        className="adminOfferItemDialog"
      >
        <DialogActions className="dialogActions">
          <Button onClick={() => setOpenOfferDetails(false)} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent>{selected?.summary}</DialogContent>
      </Dialog>
    </Box>
  );
});

export default PendingOffers;
